import LoadFailure from '../../../components/Brand/LoadFailure';
import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Alert } from 'antd';
import message from '../../../components/Brand/brandMessage';
import { flushSync } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import url from '../../../utils/url';
import { MoreIcon, ChevronUpIcon, TranslateIcon, HeartIcon, HeartFilledIcon, AlertCircleIcon, CloseIcon, BackIcon, RepostIcon, EyeOffIcon } from '../../../components/Brand/Icon';
import PostUser from '../../../components/PostUser';
import UserReactionScope from '../../../components/UserReactionScope';
import StoryTitle from '../../../components/StoryTitle';
import UnsubscribeContent from '../../../unsubscribe/UnsubscribeContent';
import Voting from '../../../components/WiwFooter/Voting';
import ButtonShare from '../../../components/Buttons/ButtonShare';
import ButtonGlaz from '../../../components/Buttons/ButtonGlaz';
import ButtonReactionAdd from '../../../components/Buttons/ButtonReactionAdd';
import { getUser, removeUser } from '../../../utils/func';
import { formatLikeCount } from '../../../utils/formatLikeCount';
import { postShare, storyShare } from '../../../utils/postShare';
import api from '../../../services';
import { useTranslation } from 'react-i18next';
import useContentOverflow from '../../../hooks/useContentOverflow';
import useReaderMotion from '../../../hooks/useReaderMotion';
import useFeedVote from '../../../hooks/useFeedVote';
import useViewerOpinion from '../../../hooks/useViewerOpinion';
import { hasViewerOpinion } from '../../../utils/viewerOpinionState';
import { useGuestTrialNotice } from '../../../guestTrial/useGuestTrialNotice';
import ReactionQuote from '../../../components/ReactionQuote';
import ReactionBody from '../../../components/ReactionQuote/ReactionBody';
import MediaViewportContext from '../../../components/ExternalLink/MediaViewportContext';
import BrandLoader from '../../../components/Brand/BrandLoader';
import useReactionChain from '../../../hooks/useReactionChain';
import { readReactionReader, saveReactionReader } from '../../../utils/reactionReaderState';
import UserActions from '../../../blocking/UserActions';
import { useBlockState } from '../../../blocking/hooks';
import { loadBlockState } from '../../../blocking/store';
import { getSessionIdentity } from '../../../hooks/useSessionIdentity';
import './ReactionContent.css';
import '../../../components/Buttons/index.scss';

const initialReaction = reaction => ({
  likeYou: Boolean(reaction.like_you), likeCount: Number(reaction.like_count) || 0,
  // Start with each author's available translation, even when the main text
  // already uses the viewer's language and has no separate translated field.
  translated: true,
});

export default function ReactionContent({ reaction, swiped = true, scope, onChangeSwipe, readerEntry, onLeaveFeed, profileUsername }) {
  const { t } = useTranslation();
  const { t: brand } = useTranslation('brand');
  const { t: blocking } = useTranslation('blocking');
  const block = useBlockState(reaction.user.id);
  const interactionBlocked = Boolean(block.interaction_blocked);
  const explainBlock = () => {
    loadBlockState(reaction.user.id, true);
    message.warning({ content: blocking('interactionBlocked') });
  };
  const navigate = useNavigate();
  const storageUser = getUser();
  const renderedIdentity = getSessionIdentity();
  const viewerOpinion = useViewerOpinion(reaction.story.id);
  const systemCard = reaction.system === 'unsubscribe';
  const [reactionData, setReactionData] = useState(() => initialReaction(reaction));
  // The current DTO is the source of the body. Copying it into state and
  // updating in an effect exposed stale link captions after an edit until
  // the next render. Only the user's translation preference belongs in state.
  const reactionText = reactionData.translated
    ? reaction.text_msg_translation || reaction.text_msg || '' : reaction.text_msg || '';
  const read = !swiped;
  const readerMotion = useReaderMotion();
  const ownCard = storageUser?.id === reaction.user.id;
  const { showNotice: showGuestNotice, startGuestAuth } = useGuestTrialNotice(reaction.story.id);
  const vote = useFeedVote({
    reactionId: systemCard ? null : reaction.id, storyId: reaction.story.id,
    initialScore: ownCard ? scope : 4,
    initialPrivacy: ownCard ? reaction.privat : undefined,
    authenticated: Boolean(storageUser?.id),
    onGuestTrial: showGuestNotice,
    onGuestLimit: startGuestAuth,
    onUnauthorized: () => {
      removeUser();
      navigate(`${url('/login')}?from=${encodeURIComponent(window.location.pathname)}`);
    },
  });
  const selfMode = vote.active && Boolean(storageUser?.id);
  // Voting on one's own post changes its rating, not its body. Keep existing
  // text/attachments visible instead of replacing them with an empty opinion.
  const showPublication = !selfMode || ownCard;
  const contentViewport = useRef(null);
  const contentBody = useRef(null);
  const chain = useReactionChain(reaction, read && showPublication);
  // Once a fresh chain says a source was hidden/removed, collapsing must not
  // reveal the older public preview that arrived with the original feed card.
  const previewQuote = chain.loaded ? chain.list[0] || null : reaction.quote;
  const hasQuote = Boolean(previewQuote);
  const hasMedia = Array.isArray(reaction.media) && reaction.media.length > 0;
  // Keep the author's complete body first in normal document flow. A quote
  // never takes space away from it; the shared reader opens all overflow.
  const publicationOverflows = useContentOverflow(contentViewport, contentBody,
    `${reactionData.translated}:${reactionText}:${previewQuote?.unavailable}:${previewQuote?.text_msg}:${previewQuote?.text_msg_translation}:${reaction.media?.map(item => `${item.id}:${item.status}`).join(',')}:${previewQuote?.media?.length}`,
    !read && showPublication && Boolean(reactionText || hasQuote || hasMedia));
  const contentOverflows = publicationOverflows || Boolean(previewQuote?.has_quote);
  const restored = useRef(readReactionReader(readerEntry, reaction.id));
  const [expandedQuotes, setExpandedQuotes] = useState(() => restored.current?.expandedQuotes || []);
  const scrollToRestore = useRef(restored.current?.scrollTop || 0);
  const rememberReader = () => saveReactionReader(readerEntry, reaction.id, {
    read, expandedQuotes, loadedQuotes: chain.list.length, scrollTop: contentViewport.current?.scrollTop || 0,
  });
  useEffect(() => {
    if (read && !chain.loading && !chain.error && chain.next != null
      && chain.list.length < (restored.current?.loadedQuotes || 0)) chain.loadMore();
  }, [read, chain]);
  useLayoutEffect(() => {
    if (read && scrollToRestore.current && contentViewport.current) {
      contentViewport.current.scrollTop = scrollToRestore.current;
      if (Math.abs(contentViewport.current.scrollTop - scrollToRestore.current) < 2
        || (!chain.loading && chain.list.length >= (restored.current?.loadedQuotes || 1))) {
        scrollToRestore.current = 0;
      }
    }
  }, [read, chain.list.length, chain.loading]);
  useEffect(() => {
    saveReactionReader(readerEntry, reaction.id, { read, expandedQuotes });
  }, [read, readerEntry, reaction.id, expandedQuotes]);
  const expandQuote = id => setExpandedQuotes(previous => previous.includes(id) ? previous : [...previous, id]);
  const toggleReading = () => {
    if (!read) restored.current = null;
    scrollToRestore.current = 0;
    readerMotion.transition(() => {
      onChangeSwipe?.(read);
      if (read && contentViewport.current) contentViewport.current.scrollTop = 0;
    });
  };
  const prepareMediaPlayback = () => {
    if (read) return;
    restored.current = null;
    scrollToRestore.current = 0;
    // Apply the reader layout before the player measures its visible area.
    flushSync(() => onChangeSwipe?.(false));
  };
  const repostPath = url(`/news/${reaction.story.id}/opinion`) + `?quote=${reaction.id}`;
  const alreadyOpined = hasViewerOpinion(reaction, storageUser?.id, viewerOpinion)
    || Boolean(storageUser?.id && (selfMode || vote.ownReactionId));
  const repostDisabled = interactionBlocked || alreadyOpined || reaction.id <= 0;
  const explainRepost = event => {
    event.preventDefault();
    if (interactionBlocked) { explainBlock(); return; }
    if (alreadyOpined) message.warning({ content: brand('repostAlreadyOpined') });
  };
  const openRepost = event => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0) return;
    rememberReader();
    if (!storageUser?.id) return;
    event.preventDefault();
    flushSync(() => navigate(repostPath, { state: { focusComposer: true, story: reaction.story,
      quote: { ...reaction, story_id: reaction.story.id, has_quote: Boolean(reaction.quote) } } }));
    document.querySelector('.comment-editor-container .ProseMirror[contenteditable="true"]')?.focus({ preventScroll: true });
  };
  const previousId = useRef(reaction.id);
  const likePending = useRef(false);
  const mounted = useRef(true);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useLayoutEffect(() => {
    if (previousId.current !== reaction.id) {
      previousId.current = reaction.id;
      setReactionData(initialReaction(reaction));
    }
  }, [reaction]);

  const like = async () => {
    if (likePending.current || renderedIdentity !== getSessionIdentity()) return;
    if (!storageUser?.id) {
      navigate(`${url('/login')}?from=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (interactionBlocked && !reactionData.likeYou) { explainBlock(); return; }
    const identity = renderedIdentity;
    const id = reaction.id;
    const before = { likeYou: reactionData.likeYou, likeCount: Number(reactionData.likeCount) || 0 };
    likePending.current = true;
    setLiking(true);
    setReactionData(prev => ({ ...prev,
      likeCount: Math.max(0, before.likeCount + (before.likeYou ? -1 : 1)), likeYou: !before.likeYou }));
    try {
      const result = await api.reactionLike({ id, is_like: !before.likeYou });
      if (!result.success) throw Object.assign(new Error('Like was not saved'), { code: result.errorCode });
    } catch (error) {
      if (mounted.current && previousId.current === id && identity === getSessionIdentity()) {
        setReactionData(prev => ({ ...prev, ...before }));
        if (error.code === 'USER_INTERACTION_BLOCKED') { loadBlockState(reaction.user.id, true); explainBlock(); }
        else message.error({ content: brand('likeError') });
      }
    } finally {
      likePending.current = false;
      if (mounted.current) setLiking(false);
    }
  };

  // The API already returns the event title in the viewer's language. Its
  // original title is not exposed; only the comment can switch to its original.
  const canTranslate = [reaction, ...(read ? chain.list : [previewQuote])].some(item =>
    item?.text_status !== 'banned' && item?.text_msg_translation && item.text_msg_translation !== item.text_msg);
  const translate = () => setReactionData(prev => ({ ...prev,
    translated: !prev.translated,
  }));
  const changeVoting = value => {
    if (read) onChangeSwipe?.(true);
    vote.changeScore(value);
  };
  const authorAction = selfMode ? (
    <button type="button" className="wiw-icon-button" aria-label={brand('returnToOpinion')}
      disabled={vote.saving} onClick={vote.reset}>
      <BackIcon className="nav-icon" />
    </button>
  ) : reaction.id > 0 && !ownCard ? (
    <UserActions user={reaction.user} reactionId={reaction.id} storyId={reaction.story.id} reportIcon />
  ) : null;
  const moderationNotice = showPublication && reaction.text_status === 'banned' &&
    <Alert message={t('reaction.banned')} type="warning" showIcon icon={<AlertCircleIcon />}
      closable={{ 'aria-label': brand('close') }} closeIcon={<CloseIcon />} />;

  const authorRow = <PostUser author={selfMode ? storageUser : reaction.user}
    scope={selfMode || ownCard ? vote.score : scope}
    storyId={reaction.story.id} reactionId={reaction.id} profileUsername={profileUsername}
    scopeInteractive={!selfMode && !systemCard} profileInteractive={!systemCard || selfMode} beforeScore={authorAction} />;

  const quotedContent = hasQuote && <div className="reaction-chain">
    {(read ? chain.list : [previewQuote]).map((quote, index) => <ReactionQuote key={quote.id} quote={quote}
      collapsed={index >= 2 && !expandedQuotes.includes(quote.id)}
      onExpand={expandQuote} onNavigate={rememberReader} original={!reactionData.translated} />)}
    {read && chain.loading && <div className="reaction-chain-feedback"><BrandLoader compact label={brand('loading')} /></div>}
    {read && chain.error && !chain.loading && <LoadFailure onRetry={chain.retry} />}
    {read && !chain.loading && !chain.error && chain.next != null && <div className="reaction-chain-feedback">
      <button type="button" onClick={chain.loadMore} aria-label={brand('quoteLoadMore')}><MoreIcon /></button>
    </div>}
  </div>;

  const leaveFeed = onLeaveFeed ? () => {
    rememberReader();
    // A pending mutation must be re-read from the server on return.
    // Accepted likes and refreshed quote visibility can be kept.
    return onLeaveFeed(vote.active || liking ? null : { ...reaction,
      like_you: reactionData.likeYou, like_count: reactionData.likeCount, quote: previewQuote });
  } : undefined;

  return (
    <div ref={readerMotion.ref} className={`reaction-wrapper reaction-card${read ? ' reaction-card-reading' : ''}`}
      data-system-card={systemCard ? 'unsubscribe' : undefined}>
      <div className="reaction-card-header">
        <StoryTitle reactionId={reaction.id} storyId={reaction.story.id} title={reaction.story.title}
          entry={systemCard ? 'unsubscribe' : null}
          onLeaveFeed={leaveFeed} />
        {!read && authorRow}
      </div>
      <div ref={contentViewport}
        className={`reaction_content loading-retained-content${read ? ' reaction-chain-scroll' : ''}${!showPublication ? ' reaction_content--own' : ''}${systemCard && showPublication ? ' reaction_content--unsubscribe' : ''}${!read && contentOverflows ? ' reaction_content--overflowing' : ''}`}
        onScroll={rememberReader} onClickCapture={rememberReader}
        tabIndex={read ? 0 : undefined}
        style={{ overflowY: !showPublication ? 'visible' : read ? 'auto' : 'hidden', ...(((!reactionText && !hasQuote && !hasMedia && reaction.text_status !== 'banned') || !showPublication) && !read && { display: 'flex' }) }}>
        {showPublication && (reactionText || hasQuote || hasMedia || read || reaction.text_status === 'banned') ? <MediaViewportContext.Provider value={prepareMediaPlayback}><div ref={contentBody} className="reaction-publication">
          {systemCard ? <UnsubscribeContent reaction={reaction} translated={reactionData.translated} onLeaveFeed={leaveFeed} />
            : <ReactionBody html={reactionText} media={reaction.media} reactionId={reaction.id} textStatus={reaction.text_status}
            quote={quotedContent} wrapContent={(body, part) => <section key={part} className="reaction-main-section"
              data-author-part={part}>
              {read && part === 'before' && <div className="reaction-main-author">{authorRow}</div>}
              <div className="reaction_content_text">
                {part === 'before' && moderationNotice}{body}
                {reaction.text_status !== 'banned' && !reactionText && !hasQuote && !hasMedia && <UserReactionScope scope={scope} size="big" interactive={false} />}
              </div>
            </section>} />}
        </div></MediaViewportContext.Provider> : <>
          {moderationNotice}
          <div className={`reaction_content_scope${selfMode ? ' reaction-own-opinion' : ''}`}
            data-privacy={selfMode ? (vote.privateOpinion ? 'hidden' : 'public') : undefined}>
            <div className={selfMode ? 'reaction-own-opinion__visual' : undefined}>
              <UserReactionScope size="big" scope={selfMode ? vote.score : scope}
                reactionId={reaction.id} interactive={!selfMode}>
                {selfMode && vote.privateOpinion ? <EyeOffIcon size={64} className="reaction-own-opinion__icon" /> : null}
              </UserReactionScope>
            </div>
            {selfMode && <div className="reaction-own-opinion__caption" role="status" aria-live="polite" aria-atomic="true">
              <p>{brand(vote.privateOpinion ? 'hiddenOpinionTitle' : 'publicOpinionTitle')}</p>
              <p>{brand(vote.privateOpinion ? 'hiddenOpinionBody' : 'publicOpinionBody')}</p>
            </div>}
          </div>
        </>}
      </div>
      <div className="control-panel-reaction" data-no-swipe>
        {systemCard && reaction.geolocation === 'db-ip' && <a className="unsubscribe-attribution"
          href="https://db-ip.com" target="_blank" rel="noopener noreferrer">IP Geolocation by DB-IP</a>}
        {selfMode && vote.error && <div className="reaction-vote-error" role="alert">
          <span>{brand('saveError')}</span>
          <button type="button" onClick={vote.retry} disabled={vote.saving}>{brand('voteRetry')}</button>
        </div>}
        <div className="reaction-actions__read">
          {(read || (showPublication && contentOverflows)) && <button type="button"
            className={`wiw-icon-button${read ? ' wiw-footer-control' : ''}`} aria-expanded={read}
            aria-label={read ? t('reaction.collapse', { defaultValue: 'Collapse reaction' }) : t('reaction.read_more', { defaultValue: 'Read full reaction' })}
            onClick={toggleReading}>
            {read ? <ChevronUpIcon className="nav-icon" /> : <MoreIcon className="nav-icon" />}
          </button>}
        </div>
        <div className="reaction-actions">
          <div className="reaction-actions__like">
            {selfMode ? <ButtonGlaz value={vote.privateOpinion} onChange={vote.togglePrivacy} /> : !systemCard &&
              <button type="button" className={`wiw-icon-button control-panel-reaction-like${interactionBlocked && !reactionData.likeYou ? ' is-interaction-blocked' : ''}`}
                aria-disabled={(interactionBlocked && !reactionData.likeYou) || undefined}
                aria-label={t('reaction.like', { defaultValue: 'Like' })}
                aria-pressed={reactionData.likeYou} disabled={liking} onClick={like}
                title={String(reactionData.likeCount)}>
                {reactionData.likeYou ? <HeartFilledIcon className="nav-icon is-like-icon" /> : <HeartIcon className="nav-icon" />}
                <span>{formatLikeCount(reactionData.likeCount)}</span>
              </button>}
          </div>
          <button type="button" className={`wiw-icon-button control-panel-reaction-translate${reactionData.translated && canTranslate && !selfMode ? ' active-translate' : ''}`}
            aria-label={t('reaction.translate', { defaultValue: 'Translate reaction' })}
            title={brand(reactionData.translated ? 'showOriginalComment' : 'translateBoth')}
            aria-pressed={!selfMode && canTranslate && reactionData.translated}
            disabled={selfMode || !canTranslate} onClick={translate}>
            <TranslateIcon className="nav-icon" />
          </button>
          <ButtonReactionAdd storyId={reaction.story.id} story={reaction.story}
            opinionIntent={selfMode ? { score: vote.score, privat: vote.privateOpinion } : undefined} />
          <Link className={`wiw-icon-button reaction-actions__repost${interactionBlocked ? ' is-interaction-blocked' : ''}`}
            to={repostDisabled ? '#' : storageUser?.id ? repostPath : `${url('/login')}?from=${encodeURIComponent(repostPath)}`}
            aria-label={brand('repostAction')} title={brand('repostAction')}
            aria-disabled={repostDisabled || undefined}
            tabIndex={repostDisabled && !alreadyOpined && !interactionBlocked ? -1 : undefined}
            onAuxClick={event => { if (repostDisabled) explainRepost(event); }}
            onClick={event => { if (repostDisabled) explainRepost(event); else openRepost(event); }}>
            <RepostIcon className="nav-icon" />
          </Link>
          <ButtonShare {...(systemCard ? storyShare(reaction.story) : postShare(reaction, {
            viewerId: storageUser?.id,
            // Hide immediately, but share the author's identity again only
            // after publishing succeeds. A failed/pending save stays private.
            privateOpinion: ownCard ? vote.privateOpinion || vote.savedPrivateOpinion : undefined,
          }))} />
        </div>
        <Voting onChange={changeVoting} onSave={vote.saveScore} scope={vote.score} />
      </div>
    </div>
  );
}
